import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { shareApi } from '../lib/api';
import { FileText, Eye, Clock, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function SharedDesignView() {
  const { shareId } = useParams();
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDesign = async () => {
      try {
        const res = await shareApi.getSharedDesign(shareId);
        setDesign(res.data);
      } catch (err) {
        if (err.response?.status === 410) {
          setError('expired');
        } else if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('unknown');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDesign();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="shared-design-error">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-stone-400" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-stone-800 mb-2">
            {error === 'expired' ? 'Link Expired' : 'Design Not Found'}
          </h1>
          <p className="text-stone-600 mb-6">
            {error === 'expired'
              ? 'This shared design link has expired. Ask the creator to share a new link.'
              : 'This design may have been removed or the link is invalid.'}
          </p>
          <Link to="/">
            <Button className="bg-primary hover:bg-primary-800 text-white" data-testid="go-home-btn">
              Go to RaiseRoot
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const renderElement = (element) => {
    const baseStyle = {
      position: 'absolute',
      left: element.x,
      top: element.y,
      width: element.width,
      height: element.height,
      ...(element.style || {}),
    };

    if (element.type === 'text') {
      return (
        <div key={element.id} style={baseStyle} data-testid="shared-element-text">
          {element.content}
        </div>
      );
    }

    if (element.type === 'image') {
      return (
        <img
          key={element.id}
          src={element.content}
          alt=""
          style={{ ...baseStyle, objectFit: 'cover' }}
          data-testid="shared-element-image"
        />
      );
    }

    if (element.type === 'shape') {
      return <div key={element.id} style={baseStyle} data-testid="shared-element-shape" />;
    }

    if (element.type === 'divider') {
      return <div key={element.id} style={baseStyle} data-testid="shared-element-divider" />;
    }

    return null;
  };

  const expiresDate = design.expires_at ? new Date(design.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-stone-100" data-testid="shared-design-page">
      {/* Header bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-lg text-primary-900">RaiseRoot</span>
            </Link>
            <span className="text-stone-300">|</span>
            <div>
              <h1 className="text-sm font-medium text-stone-800" data-testid="shared-design-name">{design.name}</h1>
              {design.org_name && (
                <p className="text-xs text-stone-500" data-testid="shared-design-org">by {design.org_name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-stone-500">
            <span className="flex items-center gap-1" data-testid="shared-view-count">
              <Eye className="w-3.5 h-3.5" /> {design.views} views
            </span>
            {expiresDate && (
              <span className="flex items-center gap-1" data-testid="shared-expires">
                <Clock className="w-3.5 h-3.5" /> Expires {expiresDate}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex justify-center py-8 px-4">
        <div
          className="bg-white shadow-xl relative"
          data-testid="shared-design-canvas"
          style={{
            width: design.width,
            height: design.height,
            backgroundColor: design.background_color || '#ffffff',
          }}
        >
          {(design.elements || []).map(renderElement)}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pb-8">
        <p className="text-xs text-stone-400">
          View only — Created with{' '}
          <Link to="/" className="text-primary hover:underline">RaiseRoot</Link>
        </p>
      </div>
    </div>
  );
}
